import type { CategorySpend } from '@/api/dashboard'
import { fromPaise, toPaise } from '@/lib/splitMath'

export interface CategoryChangeRow extends CategorySpend {
  /** Null when there's no previous period to compare with. */
  change: number | null
  /** Nothing in this category last period. */
  isNew: boolean
}

export interface CategoryComparison {
  rows: CategoryChangeRow[]
  total: number
  previousTotal: number | null
  grewMost: CategoryChangeRow | null
}

const key = (c: CategorySpend) => c.categoryId ?? 'uncategorised'

function positiveTotal(list: CategorySpend[]): number {
  return fromPaise(list.filter((c) => c.total > 0).reduce((sum, c) => sum + toPaise(c.total), 0))
}

/**
 * Pairs each category with its previous-period total. Categories refunds pushed net-negative
 * aren't a slice of spending, so they're dropped and left out of both totals.
 */
export function compareWithPrevious(
  current: CategorySpend[],
  previous: CategorySpend[] | undefined
): CategoryComparison {
  const before = previous ? new Map(previous.map((c) => [key(c), c.total])) : null
  const rows = current
    .filter((c) => c.total > 0)
    .map((c) => {
      if (!before) return { ...c, change: null, isNew: false }
      const last = before.get(key(c))
      return {
        ...c,
        change: fromPaise(toPaise(c.total) - toPaise(last ?? 0)),
        isNew: last == null || last <= 0,
      }
    })
  const grewMost = rows.reduce<CategoryChangeRow | null>(
    (best, r) => (r.change != null && r.change > 0 && r.change > (best?.change ?? 0) ? r : best),
    null
  )
  return {
    rows,
    total: positiveTotal(current),
    previousTotal: previous ? positiveTotal(previous) : null,
    grewMost,
  }
}
